import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Plus, Trash2, BookOpen, Upload, Music, Image as ImageIcon, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import ExerciseCard from './ExerciseCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LessonsTab({ lessons, onUpdate }) {
  const [showLessonForm, setShowLessonForm] = useState(false);
  const [newLesson, setNewLesson] = useState({ title: '', description: '' });
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [exercises, setExercises] = useState({});
  const [showExerciseForm, setShowExerciseForm] = useState(null);
  const [newExercise, setNewExercise] = useState({
    title: '',
    description: '',
    text_content: '',
    text_layout: 'single',
    order: 0
  });

  const createLesson = async () => {
    if (!newLesson.title || !newLesson.description) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API}/lessons`, newLesson);
      setNewLesson({ title: '', description: '' });
      setShowLessonForm(false);
      toast.success('Lesson created!');
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create lesson');
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm('Delete this lesson and ALL its exercises?')) {
      return;
    }

    try {
      await axios.delete(`${API}/lessons/${lessonId}`);
      toast.success('Lesson deleted');
      onUpdate();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete lesson');
    }
  };

  const loadExercises = async (lessonId) => {
    try {
      const response = await axios.get(`${API}/lessons/${lessonId}/exercises`);
      setExercises(prev => ({...prev, [lessonId]: response.data}));
    } catch (error) {
      console.error('Error loading exercises:', error);
      toast.error('Failed to load exercises');
    }
  };

  const createExercise = async (lessonId) => {
    if (!newExercise.title || !newExercise.description) {
      toast.error('Please fill in title and description');
      return;
    }

    try {
      await axios.post(`${API}/exercises`, {
        ...newExercise,
        lesson_id: lessonId
      });
      setNewExercise({ title: '', description: '', text_content: '', text_layout: 'single', order: 0 });
      setShowExerciseForm(null);
      toast.success('Exercise created!');
      loadExercises(lessonId);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create exercise');
    }
  };

  const deleteExercise = async (lessonId, exerciseId) => {
    if (!window.confirm('Delete this exercise?')) {
      return;
    }

    try {
      await axios.delete(`${API}/exercises/${exerciseId}`);
      toast.success('Exercise deleted');
      loadExercises(lessonId);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete exercise');
    }
  };

  useEffect(() => {
    if (expandedLesson && !exercises[expandedLesson]) {
      loadExercises(expandedLesson);
    }
  }, [expandedLesson]);

  return (
    <div className="space-y-6">
      {/* Create Lesson Form */}
      {!showLessonForm ? (
        <Card>
          <CardContent className="pt-6">
            <Button
              onClick={() => setShowLessonForm(true)}
              className="w-full"
              data-testid="show-lesson-form-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Lesson
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add New Lesson
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="lesson-title">Lesson Title</Label>
              <Input
                id="lesson-title"
                placeholder="e.g., Beginning Sounds"
                value={newLesson.title}
                onChange={(e) => setNewLesson({...newLesson, title: e.target.value})}
                data-testid="lesson-title-input"
              />
            </div>
            <div>
              <Label htmlFor="lesson-desc">Description</Label>
              <Textarea
                id="lesson-desc"
                placeholder="Brief description of the lesson..."
                value={newLesson.description}
                onChange={(e) => setNewLesson({...newLesson, description: e.target.value})}
                rows={3}
                data-testid="lesson-desc-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={createLesson} className="flex-1" data-testid="create-lesson-btn">
                Create Lesson
              </Button>
              <Button variant="outline" onClick={() => setShowLessonForm(false)} data-testid="cancel-lesson-btn">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lessons List with Exercises */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            All Lessons ({lessons.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="space-y-4">
            {lessons.map((lesson, lessonIndex) => {
              const lessonBgColor = lessonIndex % 2 === 0 ? 'bg-blue-50' : 'bg-purple-50';
              return (
              <AccordionItem key={lesson.id} value={lesson.id} className={`border rounded-lg ${lessonBgColor}`}>
                <AccordionTrigger
                  className="px-4 hover:no-underline"
                  onClick={() => setExpandedLesson(lesson.id)}
                  data-testid={`lesson-item-${lessonIndex}`}
                >
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="text-left">
                      <h3 className="font-semibold" data-testid={`lesson-title-${lessonIndex}`}>{lesson.title}</h3>
                      <p className="text-sm text-gray-600 mt-1" data-testid={`lesson-desc-${lessonIndex}`}>
                        {lesson.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">
                        {exercises[lesson.id]?.length || 0} exercises
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteLesson(lesson.id);
                        }}
                        data-testid={`delete-lesson-btn-${lessonIndex}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-4 mt-4">
                    {/* Add Exercise Button */}
                    {showExerciseForm !== lesson.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowExerciseForm(lesson.id)}
                        data-testid={`show-exercise-form-btn-${lessonIndex}`}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Exercise
                      </Button>
                    )}

                    {/* Add Exercise Form */}
                    {showExerciseForm === lesson.id && (
                      <Card className="bg-blue-50">
                        <CardContent className="pt-4 space-y-3">
                          <Input
                            placeholder="Exercise title"
                            value={newExercise.title}
                            onChange={(e) => setNewExercise({...newExercise, title: e.target.value})}
                            data-testid={`exercise-title-input-${lessonIndex}`}
                          />
                          <Textarea
                            placeholder="Exercise description"
                            value={newExercise.description}
                            onChange={(e) => setNewExercise({...newExercise, description: e.target.value})}
                            rows={2}
                            data-testid={`exercise-desc-input-${lessonIndex}`}
                          />
                          <div>
                            <Label className="text-xs mb-2 block">Practice Text (Optional)</Label>
                            <Textarea
                              placeholder="Enter practice text. Use line breaks for paragraphs..."
                              value={newExercise.text_content}
                              onChange={(e) => setNewExercise({...newExercise, text_content: e.target.value})}
                              rows={5}
                              className="font-mono"
                              data-testid={`exercise-text-input-${lessonIndex}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs mb-2 block">Text Layout</Label>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant={newExercise.text_layout === 'single' ? 'default' : 'outline'}
                                onClick={() => setNewExercise({...newExercise, text_layout: 'single'})}
                                data-testid={`layout-single-btn-${lessonIndex}`}
                              >
                                Single Column
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant={newExercise.text_layout === 'two-column' ? 'default' : 'outline'}
                                onClick={() => setNewExercise({...newExercise, text_layout: 'two-column'})}
                                data-testid={`layout-two-column-btn-${lessonIndex}`}
                              >
                                Two Columns
                              </Button>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => createExercise(lesson.id)}
                              className="flex-1"
                              data-testid={`create-exercise-btn-${lessonIndex}`}
                            >
                              Create Exercise
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowExerciseForm(null)}
                              data-testid={`cancel-exercise-btn-${lessonIndex}`}
                            >
                              Cancel
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Exercises List */}
                    <div className="space-y-3">
                      {exercises[lesson.id]?.map((exercise, exIndex) => (
                        <ExerciseCard
                          key={exercise.id}
                          exercise={exercise}
                          lessonId={lesson.id}
                          lessonIndex={lessonIndex}
                          exIndex={exIndex}
                          isEven={exIndex % 2 === 0}
                          onUpdate={() => loadExercises(lesson.id)}
                          onDelete={() => deleteExercise(lesson.id, exercise.id)}
                        />
                      ))}
                      {exercises[lesson.id]?.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">
                          No exercises yet. Add your first exercise above!
                        </p>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              );
            })}
          </Accordion>
          {lessons.length === 0 && (
            <p className="text-center text-gray-400 py-8" data-testid="no-lessons-message">
              No lessons yet. Create your first lesson above!
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
